import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";

const SURFACE = {
  neutral: "bg-card text-card-text",
  primary: "bg-primary text-primary-foreground",
  inverse: "bg-gray-scale-900 text-on-media",
} as const;

/** Classes of the `Balloon` bubble. */
export const balloonVariants = cva("relative w-fit max-w-xs rounded-card px-3 py-2 text-sm font-medium", {
  variants: {
    tone: {
      neutral: [SURFACE.neutral, "shadow-raised"],
      primary: [SURFACE.primary, "shadow-raised"],
      inverse: [SURFACE.inverse, "shadow-raised"],
    },
  },
  defaultVariants: { tone: "neutral" },
});

/** Classes of the `Balloon` arrow: placed on the edge facing the anchor. */
export const balloonArrowVariants = cva("absolute h-3 w-3 rotate-45", {
  variants: {
    side: {
      top: "-bottom-1.5",
      bottom: "-top-1.5",
      left: "-right-1.5",
      right: "-left-1.5",
    },
    align: { start: "", center: "", end: "" },
  },
  compoundVariants: [
    { side: ["top", "bottom"], align: "start", class: "left-4" },
    { side: ["top", "bottom"], align: "center", class: "left-1/2 -translate-x-1/2" },
    { side: ["top", "bottom"], align: "end", class: "right-4" },
    { side: ["left", "right"], align: "start", class: "top-3" },
    { side: ["left", "right"], align: "center", class: "top-1/2 -translate-y-1/2" },
    { side: ["left", "right"], align: "end", class: "bottom-3" },
  ],
  defaultVariants: { side: "top", align: "center" },
});

/** Props of `Balloon`; `side` is where the bubble sits relative to its anchor. */
export type BalloonProps = VariantProps<typeof balloonVariants> &
  VariantProps<typeof balloonArrowVariants> &
  HTMLAttributes<HTMLDivElement>;

/** Speech-bubble hint with an arrow; with an `id` it is exposed as a tooltip for `aria-describedby`. */
export const Balloon = forwardRef<HTMLDivElement, BalloonProps>(function Balloon(
  { tone, side, align, id, className, children, ...rest },
  ref,
) {
  return (
    <div
      {...rest}
      ref={ref}
      id={id}
      role={id ? "tooltip" : undefined}
      className={cn(balloonVariants({ tone }), className)}
    >
      <span aria-hidden className={cn(balloonArrowVariants({ side, align }), SURFACE[tone ?? "neutral"])} />
      <span className="relative">{children}</span>
    </div>
  );
});
